"use client";

import React from "react";
import { useIsMobile } from "../hooks/use-mobile";
import { ChevronDown, ChevronUp } from "lucide-react";
import MainLayout from "../components/MainLayout";

const ChildSafetyPolicy = () => {
  const isMobile = useIsMobile();
  const [expandedSections, setExpandedSections] = React.useState({});

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const isSectionExpanded = (sectionId) => {
    return expandedSections[sectionId] !== false;
  };

  const renderSection = (id, title, content, level = 2) => {
    const isExpanded = isSectionExpanded(id);
    const HeadingTag = `h${level}`;

    return (
      <div id={id} className="mb-8">
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={() => toggleSection(id)}
        >
          <HeadingTag
            className={`font-bold ${
              level === 1 ? "text-2xl" : level === 2 ? "text-xl" : "text-lg"
            } mb-2`}
          >
            {title}
          </HeadingTag>
          {isMobile && (
            <button className="p-1 text-gray-500">
              {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
          )}
        </div>
        {(!isMobile || isExpanded) && (
          <div className="text-gray-600">{content}</div>
        )}
      </div>
    );
  };

  return (
    <MainLayout rs={false}>
      <div className="pl-2 max-w-4xl mx-auto font-afacad">
        <div className="mt-4 mb-10">
          <h1 className="text-3xl font-bold mb-2 font-fraunces">
            CHILD SAFETY STANDARDS & CSAE POLICY
          </h1>
          <div className="text-gray-500 mb-4">
            <p>App Name: Gallyo</p>
            <p>Developer Name: Opsaway.AI</p>
            <p>Last Updated: January 2026</p>
          </div>
        </div>

        <div className="mb-8">
          <p className="text-gray-600">
            Gallyo is committed to protecting children and maintaining a safe
            platform for all users. This policy outlines our standards and
            procedures for preventing child sexual abuse and exploitation (CSAE)
            on our platform.
          </p>
        </div>

        {renderSection(
          "commitment",
          "1. COMMITMENT TO CHILD SAFETY",
          <div>
            <p className="mb-4">
              Gallyo is committed to protecting children and maintaining a safe
              platform for all users. We have zero tolerance for any form of
              child sexual abuse, exploitation, or endangerment (CSAE).
            </p>
            <p className="mb-4">
              We strictly prohibit any content, behavior, or interaction that
              exploits or harms minors.
            </p>
          </div>
        )}

        {renderSection(
          "prohibited",
          "2. PROHIBITED CONTENT AND BEHAVIOR",
          <div>
            <p className="mb-4">
              The following activities are strictly forbidden on Gallyo:
            </p>
            <ul className="list-disc pl-8 mb-4 text-gray-600">
              <li>Child sexual abuse material (CSAM) in any form</li>
              <li>Sexual content involving minors</li>
              <li>Grooming, solicitation, or exploitation of minors</li>
              <li>
                Requests for sexual images, videos, or conversations involving
                minors
              </li>
              <li>
                Any content or behavior that violates child protection laws
              </li>
            </ul>
            <p className="mb-4">
              Accounts engaging in such activities will be immediately suspended
              or permanently banned.
            </p>
          </div>
        )}

        {renderSection(
          "reporting",
          "3. REPORTING AND ENFORCEMENT",
          <div>
            <p className="mb-4">
              Users can report child safety concerns directly within the app
              using the in-app reporting feature.
            </p>
            <p className="mb-4">
              All reports are reviewed promptly by our moderation team. We take
              appropriate enforcement actions, including:
            </p>
            <ul className="list-disc pl-8 mb-4 text-gray-600">
              <li>Content removal</li>
              <li>Account suspension or termination</li>
              <li>
                Cooperation with law enforcement authorities when required by law
              </li>
            </ul>
          </div>
        )}

        {renderSection(
          "contact",
          "4. CONTACT FOR CHILD SAFETY CONCERNS",
          <div>
            <p className="mb-4">
              For urgent child safety concerns or CSAE reports, contact us at:
            </p>
            <p className="mb-4">
              <a
                href="mailto:support@opsaway.ai"
                className="text-blue-600 hover:underline"
              >
                📧 support@opsaway.ai
              </a>
            </p>
            <p className="mb-4">
              We respond to child safety reports as a priority.
            </p>
          </div>
        )}

        {renderSection(
          "compliance",
          "5. COMPLIANCE WITH LAWS AND REGULATIONS",
          <div>
            <p className="mb-4">
              Gallyo complies with all applicable local and international child
              safety laws and regulations. We cooperate fully with law
              enforcement agencies in investigations related to CSAE.
            </p>
          </div>
        )}

        {renderSection(
          "improvement",
          "6. CONTINUOUS IMPROVEMENT",
          <div>
            <p className="mb-4">
              We continuously improve our safety measures, moderation practices,
              and reporting tools to maintain a safe environment for all users.
            </p>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default ChildSafetyPolicy;

